/**
 * EditEntryModal.tsx
 * Tap-to-edit modal for a single Flight / Fuel / Expense row owned by the
 * caller in the current quarter.
 *
 * Three modes share one modal:
 *   - 'edit'    — field form for the current tab
 *   - 'confirm-delete' — second-step confirmation before deleting
 *   - 'busy'    — save or delete in flight
 *
 * The receipt replacement flow (Fuel/Expense only) is wired through the
 * existing PhotoCapture component → presigned PUT → S3, then the new key is
 * sent in `fields.receiptUrl` on the update request.
 *
 * Shared-flight constraint: this modal edits ONE row. If a flight was logged
 * with co-partners, those partners' rows are separate and untouched here.
 * To re-split, the caller must delete + relog.
 */

import { useState } from 'react'
import {
  updateFlight,
  updateFuel,
  updateExpense,
  deleteEntry as apiDeleteEntry,
  ApiError,
} from '../api'
import PhotoCapture from './PhotoCapture'
import type { EntryRow, EntryTab } from '../types'

interface EditEntryModalProps {
  tab: EntryTab;
  row: EntryRow;
  /** Caller's partner ID (used as a tag for the receipt filename). */
  callerPartnerId: string;
  onClose: () => void;
  /** Called after a successful save or delete so the parent refetches. */
  onSaved: () => void;
}

type Mode = 'edit' | 'confirm-delete' | 'busy'

// ---- helpers ---------------------------------------------------------------

function parseRowIndex(row: EntryRow): number | null {
  const raw = row._rowIndex
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n >= 2 ? n : null
}

function roundTenth(x: number): number { return Math.round(x * 10) / 10 }

// ---- Component -------------------------------------------------------------

export default function EditEntryModal({
  tab,
  row,
  callerPartnerId,
  onClose,
  onSaved,
}: EditEntryModalProps) {
  const rowIndex = parseRowIndex(row)
  const [mode, setMode] = useState<Mode>('edit')
  const [error, setError] = useState('')

  // ---- Shared "Date" field
  const [date, setDate] = useState(row['Date'] ?? '')

  // ---- Flight fields
  const [tachStart, setTachStart] = useState(row['TachStart'] ?? '')
  const [tachEnd, setTachEnd] = useState(row['TachEnd'] ?? '')

  // ---- Fuel fields
  const [airport, setAirport] = useState(row['Airport'] ?? '')
  const [gallons, setGallons] = useState(row['Gallons'] ?? '')
  const [totalCost, setTotalCost] = useState(row['TotalCost'] ?? '')

  // ---- Expense fields
  const [description, setDescription] = useState(row['Description'] ?? '')
  const [amount, setAmount] = useState(row['Amount'] ?? '')

  // ---- Notes (all tabs)
  const [notes, setNotes] = useState(row['Notes'] ?? '')

  // ---- Receipt replacement (Fuel / Expense)
  //
  // `receiptKey` is the in-flight result from a fresh PhotoCapture upload.
  // When undefined, we send `receiptUrl: undefined` so the server keeps the
  // existing cell. When set, we send the new s3://… URI built from the key.
  const [newReceiptKey, setNewReceiptKey] = useState<string | undefined>(undefined)

  if (rowIndex == null) {
    // Defensive: should never happen since the parent only opens the modal
    // for rows that have _rowIndex, but render a clear message just in case.
    return (
      <div className="modal-backdrop" role="dialog" aria-modal="true">
        <div className="modal-card">
          <h2 className="modal-title">Cannot edit this row</h2>
          <p className="field-note">Row identifier missing. Reload the page.</p>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    )
  }

  // ---- Submit handlers -----------------------------------------------------

  async function handleSave() {
    setError('')

    // Per-tab validation, mirroring the log forms.
    if (!date) return setError('Date is required.')

    if (tab === 'flights') {
      const start = parseFloat(tachStart)
      const end = parseFloat(tachEnd)
      if (isNaN(start) || isNaN(end)) return setError('Tach values must be numbers.')
      if (end < start) return setError('Tach End must be ≥ Tach Start.')
      const hours = roundTenth(end - start)
      if (hours <= 0) return setError('Flight must have at least 0.1 hours.')

      setMode('busy')
      try {
        const res = await updateFlight(rowIndex!, {
          date, tachStart: start, tachEnd: end, notes,
        })
        if (!res.ok) { setError(res.message || 'Could not save.'); setMode('edit'); return }
        onSaved()
      } catch (err) { handleError(err) }
      return
    }

    if (tab === 'fuel') {
      const gal = parseFloat(gallons)
      if (!gallons || isNaN(gal) || gal <= 0)
        return setError('Gallons must be a positive number.')
      const cost = parseFloat(totalCost)
      if (!totalCost || isNaN(cost) || cost <= 0)
        return setError('Total Cost must be a positive number.')

      setMode('busy')
      try {
        const receiptUrl = newReceiptKey
          ? buildS3UriFromKey(newReceiptKey)
          : undefined
        const res = await updateFuel(rowIndex!, {
          date,
          airport,
          gallons: gal,
          totalCost: cost,
          notes,
          ...(receiptUrl !== undefined ? { receiptUrl } : {}),
        })
        if (!res.ok) { setError(res.message || 'Could not save.'); setMode('edit'); return }
        onSaved()
      } catch (err) { handleError(err) }
      return
    }

    // expenses
    if (!description.trim()) return setError('Description is required.')
    const amt = parseFloat(amount)
    if (amount === '' || isNaN(amt) || amt < 0)
      return setError('Amount must be a non-negative number.')

    setMode('busy')
    try {
      const receiptUrl = newReceiptKey
        ? buildS3UriFromKey(newReceiptKey)
        : undefined
      const res = await updateExpense(rowIndex!, {
        date,
        category: 'Variable',
        description: description.trim(),
        amount: amt,
        notes,
        ...(receiptUrl !== undefined ? { receiptUrl } : {}),
      })
      if (!res.ok) { setError(res.message || 'Could not save.'); setMode('edit'); return }
      onSaved()
    } catch (err) { handleError(err) }
  }

  async function handleConfirmDelete() {
    setError('')
    setMode('busy')
    try {
      const res = await apiDeleteEntry({ tab, rowIndex: rowIndex! })
      if (!res.ok) { setError(res.message || 'Could not delete.'); setMode('confirm-delete'); return }
      onSaved()
    } catch (err) { handleError(err) }
  }

  function handleError(err: unknown) {
    if (err instanceof ApiError && err.status === 403) {
      setError('Not allowed: only the row owner can edit, and only in the current quarter.')
    } else if (err instanceof ApiError) {
      setError(err.message)
    } else {
      setError(err instanceof Error ? err.message : 'Unexpected error.')
    }
    setMode('edit')
  }

  // ---- Render --------------------------------------------------------------

  const busy = mode === 'busy'

  if (mode === 'confirm-delete') {
    return (
      <div className="modal-backdrop" role="dialog" aria-modal="true">
        <div className="modal-card">
          <h2 className="modal-title">Delete this entry?</h2>
          <div className="modal-summary">
            <div className="modal-summary-row">
              <span className="modal-summary-label">Date</span>
              <span className="modal-summary-value">{row['Date'] ?? '—'}</span>
            </div>
            {tab === 'flights' && (
              <div className="modal-summary-row">
                <span className="modal-summary-label">Hours</span>
                <span className="modal-summary-value">{row['Hours'] ?? '—'}</span>
              </div>
            )}
            {tab === 'fuel' && (
              <div className="modal-summary-row">
                <span className="modal-summary-label">Total</span>
                <span className="modal-summary-value">${row['TotalCost'] ?? '—'}</span>
              </div>
            )}
            {tab === 'expenses' && (
              <div className="modal-summary-row">
                <span className="modal-summary-label">Amount</span>
                <span className="modal-summary-value">${row['Amount'] ?? '—'}</span>
              </div>
            )}
          </div>
          {error && <div className="msg msg-err">{error}</div>}
          <p className="field-note">This cannot be undone.</p>
          <div className="modal-actions">
            <button className="btn btn-expense" onClick={handleConfirmDelete} disabled={busy}>
              Yes, delete
            </button>
            <button className="btn btn-secondary" onClick={() => setMode('edit')} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-card">
        <h2 className="modal-title">
          Edit {tab === 'flights' ? 'flight' : tab === 'fuel' ? 'fuel' : 'expense'}
        </h2>

        {error && <div className="msg msg-err">{error}</div>}

        <label htmlFor="edit-date">Date</label>
        <input
          id="edit-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={busy}
        />

        {tab === 'flights' && (
          <>
            <div className="row2">
              <div>
                <label htmlFor="edit-tachStart">Tach Start</label>
                <input
                  id="edit-tachStart"
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={tachStart}
                  onChange={(e) => setTachStart(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div>
                <label htmlFor="edit-tachEnd">Tach End</label>
                <input
                  id="edit-tachEnd"
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={tachEnd}
                  onChange={(e) => setTachEnd(e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>
            <label>Hours (computed)</label>
            <div className="computed-display">
              {computeHoursDisplay(tachStart, tachEnd)}
            </div>
          </>
        )}

        {tab === 'fuel' && (
          <>
            <label htmlFor="edit-airport">Airport</label>
            <input
              id="edit-airport"
              type="text"
              value={airport}
              onChange={(e) => setAirport(e.target.value.toUpperCase())}
              maxLength={10}
              disabled={busy}
            />
            <label htmlFor="edit-gallons">Gallons</label>
            <input
              id="edit-gallons"
              type="number"
              step="0.01"
              inputMode="decimal"
              value={gallons}
              onChange={(e) => setGallons(e.target.value)}
              disabled={busy}
            />
            <label htmlFor="edit-totalCost">Total Cost ($)</label>
            <input
              id="edit-totalCost"
              type="number"
              step="0.01"
              inputMode="decimal"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              disabled={busy}
            />
            <label>Replace receipt photo (optional)</label>
            <PhotoCapture
              label="fuel"
              date={date}
              partnerId={callerPartnerId}
              onUploadComplete={(key) => setNewReceiptKey(key)}
            />
            {row['ReceiptURL'] && !newReceiptKey && (
              <p className="field-note">
                Current receipt:{' '}
                <a href={row['ReceiptURL']} target="_blank" rel="noopener noreferrer">view</a>
              </p>
            )}
          </>
        )}

        {tab === 'expenses' && (
          <>
            <label htmlFor="edit-description">Description</label>
            <input
              id="edit-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
            />
            <label htmlFor="edit-amount">Amount ($)</label>
            <input
              id="edit-amount"
              type="number"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
            />
            <label>Replace receipt photo (optional)</label>
            <PhotoCapture
              label="expense"
              date={date}
              partnerId={callerPartnerId}
              onUploadComplete={(key) => setNewReceiptKey(key)}
            />
            {row['ReceiptURL'] && !newReceiptKey && (
              <p className="field-note">
                Current receipt:{' '}
                <a href={row['ReceiptURL']} target="_blank" rel="noopener noreferrer">view</a>
              </p>
            )}
          </>
        )}

        <label htmlFor="edit-notes">Notes</label>
        <textarea
          id="edit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={busy}
        />

        <div className="modal-actions">
          <button
            className={`btn ${tab === 'flights' ? 'btn-flight' : tab === 'fuel' ? 'btn-fuel' : 'btn-expense'}`}
            onClick={handleSave}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-neutral"
            onClick={() => setMode('confirm-delete')}
            disabled={busy}
          >
            Delete…
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Local helpers ---------------------------------------------------------

function computeHoursDisplay(start: string, end: string): string {
  const s = parseFloat(start)
  const e = parseFloat(end)
  if (isNaN(s) || isNaN(e) || e < s) return '—'
  return `${roundTenth(e - s).toFixed(1)} hrs`
}

/**
 * PhotoCapture returns the bare S3 object key (e.g. "receipts/2026/06/uuid.jpg").
 * We forward it as-is to the update endpoint; the column stores whatever the
 * submit handlers store today. (Submit handlers currently have a known
 * field-name mismatch — client sends `receiptKey`, server reads `receiptUrl`,
 * so the ReceiptURL column is usually blank. That bug is pre-existing and
 * out of scope for tap-to-edit; this code mirrors the existing pattern.)
 */
function buildS3UriFromKey(key: string): string {
  return key
}
